import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { trackCartEvent } from '../hooks/useAnalytics';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const SEMI_GROS_MIN_TOTAL = 12;     // Total cart items to reach semi-gros tier
const GROS_MIN_TOTAL = 60;          // Total cart items to override everything to gros
const PRODUCT_GROS_MIN = 12;        // Per-product quantity to qualify for gros price

const FORCED_TIERS = new Set(['semi_gros', 'gros', 'vip']);

// Null-safe tier pricing. The backend hides wholesale prices (null) for
// guests and non-approved accounts — without a fallback the cart crashed
// with "Cannot read properties of null (reading 'toFixed')".
const _tierUnit = (product, tier) => {
  const detail = Number(product?.price_detail) || 0;
  const semi = product?.price_semi_gros;
  const gros = product?.price_gros;
  if (tier === 'gros') return Number(gros ?? semi ?? detail) || detail;
  if (tier === 'semi_gros') return Number(semi ?? detail) || detail;
  return detail;
};

const _vipUnit = (product) => {
  const cp = Number(product?.cost_price || 0);
  if (cp <= 0) return null;
  const m = product?.vip_margin == null || product.vip_margin === ''
    ? 1
    : Number(product.vip_margin);
  const safe = Number.isFinite(m) && m >= 0 ? m : 1;
  return Math.round((cp + safe) * 100) / 100;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth() || {};
  // Wholesale tiers are reserved to approved customers and staff — the API
  // returns null wholesale prices for everyone else, so the cart must stay
  // on the public retail tier for them.
  const wholesaleAllowed = !!user && (
    user.role === 'admin' || user.role === 'employee' || user.status === 'approved'
  );
  const forcedTier = wholesaleAllowed && FORCED_TIERS.has(user.pricing_tier) ? user.pricing_tier : null;

  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [promoApplied, setPromoApplied] = useState(null);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  const totalItems = useMemo(() => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }, [items]);

  // Pricing rules (hybrid):
  //  - User has a forced tier → every line uses that tier (any quantity)
  //  - Total cart < 12          → all items at DETAIL price
  //  - Total cart ≥ 60          → all items at GROS price (override)
  //  - Otherwise (12-59)        → per product: qty ≥ 12 of same product → GROS,
  //                               else SEMI_GROS
  const cartLevelTier = useMemo(() => {
    if (forcedTier) return forcedTier;
    if (!wholesaleAllowed) return 'detail';
    if (totalItems >= GROS_MIN_TOTAL) return 'gros';
    if (totalItems < SEMI_GROS_MIN_TOTAL) return 'detail';
    return 'mixed'; // semi_gros default, but per-product gros possible
  }, [totalItems, forcedTier, wholesaleAllowed]);

  const isSemiGrosUnlocked = cartLevelTier !== 'detail';
  const isGrosUnlocked = cartLevelTier === 'gros' || forcedTier === 'gros' || forcedTier === 'vip';

  // Compute the tier of a single line item, given the current cart context.
  const getItemTier = useCallback((item) => {
    if (forcedTier) return forcedTier;
    if (cartLevelTier === 'gros') return 'gros';
    if (cartLevelTier === 'detail') return 'detail';
    // mixed: per-product
    return item.quantity >= PRODUCT_GROS_MIN ? 'gros' : 'semi_gros';
  }, [cartLevelTier, forcedTier]);

  // For backward compat — currentTier represents the cheapest tier currently active
  // (used by tier banners and "items until next tier" UI).
  const currentTier = useMemo(() => {
    if (forcedTier) return forcedTier;
    if (cartLevelTier === 'gros') return 'gros';
    if (cartLevelTier === 'detail') return 'detail';
    // mixed → semi_gros at minimum (some items may already be gros via per-product)
    return 'semi_gros';
  }, [cartLevelTier, forcedTier]);

  const addToCart = useCallback((product, quantity = 1) => {
    trackCartEvent(product?.id, 'add', quantity);
    setItems(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(item => item.product.id !== productId));
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  }, []);

  const removeFromCart = useCallback((productId) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setPromoApplied(null);
  }, []);

  // Price: per-item, computed from the item's individual tier
  const getItemPrice = useCallback((item) => {
    const tier = getItemTier(item);
    if (tier === 'vip') {
      const vip = _vipUnit(item.product);
      // If cost_price not available client-side (typical for public customers),
      // fall back to gros price. The server will still charge the real VIP.
      return vip != null ? vip : _tierUnit(item.product, 'gros');
    }
    return _tierUnit(item.product, tier);
  }, [getItemTier]);

  const getPriceTier = useCallback((item) => {
    if (item) return getItemTier(item);
    return currentTier;
  }, [currentTier, getItemTier]);

  /**
   * Preview the tier and unit price the user would get if they added
   * `addQty` of `product` to the current cart. Used by the product detail
   * page so the displayed total matches what the cart will actually charge.
   */
  const getPreviewPrice = useCallback((product, addQty) => {
    if (!product || !addQty || addQty < 1) {
      return { tier: 'detail', unit: product?.price_detail || 0, total: 0 };
    }
    const existing = items.find((it) => it.product.id === product.id);
    const finalQtyOfThisProduct = (existing?.quantity || 0) + addQty;
    const finalCartTotal = totalItems + addQty;

    let tier;
    if (forcedTier) {
      tier = forcedTier;
    } else if (!wholesaleAllowed) {
      tier = 'detail';
    } else if (finalCartTotal >= GROS_MIN_TOTAL) {
      tier = 'gros';
    } else if (finalCartTotal < SEMI_GROS_MIN_TOTAL) {
      tier = 'detail';
    } else {
      tier = finalQtyOfThisProduct >= PRODUCT_GROS_MIN ? 'gros' : 'semi_gros';
    }

    let unit;
    if (tier === 'vip') {
      const vip = _vipUnit(product);
      unit = vip != null ? vip : _tierUnit(product, 'gros');
    } else {
      unit = _tierUnit(product, tier);
    }
    return { tier, unit, total: unit * addQty, finalCartTotal, finalQtyOfThisProduct };
  }, [items, totalItems, forcedTier, wholesaleAllowed]);

  const getSubtotal = useCallback(() => {
    return items.reduce((total, item) => {
      return total + (getItemPrice(item) * item.quantity);
    }, 0);
  }, [items, getItemPrice]);

  const getTotalItems = useCallback(() => totalItems, [totalItems]);

  const itemsUntilNextTier = useMemo(() => {
    if (forcedTier || !wholesaleAllowed) return 0;  // No tier progression to advertise
    if (currentTier === 'gros') return 0;
    if (currentTier === 'semi_gros') return GROS_MIN_TOTAL - totalItems;
    return SEMI_GROS_MIN_TOTAL - totalItems;
  }, [currentTier, totalItems, forcedTier, wholesaleAllowed]);

  const nextTierName = useMemo(() => {
    if (forcedTier || !wholesaleAllowed) return null;
    if (currentTier === 'detail') return 'semi_gros';
    if (currentTier === 'semi_gros') return 'gros';
    return null;
  }, [currentTier, forcedTier, wholesaleAllowed]);

  const value = useMemo(() => ({
    items, addToCart, updateQuantity, removeFromCart, clearCart,
    getItemPrice, getPriceTier, getItemTier, getSubtotal, getTotalItems,
    getPreviewPrice,
    isSemiGrosUnlocked, isGrosUnlocked, currentTier, totalItems,
    itemsUntilNextTier, nextTierName,
    forcedTier, wholesaleAllowed,
    SEMI_GROS_MIN_TOTAL, GROS_MIN_TOTAL, PRODUCT_GROS_MIN,
    promoApplied, setPromoApplied
  }), [items, addToCart, updateQuantity, removeFromCart, clearCart,
    getItemPrice, getPriceTier, getItemTier, getSubtotal, getTotalItems,
    getPreviewPrice,
    isSemiGrosUnlocked, isGrosUnlocked, currentTier, totalItems,
    itemsUntilNextTier, nextTierName, forcedTier, wholesaleAllowed, promoApplied]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
